import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Tabs, ButtonLink, TextLink } from '@shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { ProductDocumentsPanel } from '@/features/document-templates';
import { useDealerQuery } from '@/features/dealers/hooks/useDealerQuery';
import { useProductQuery } from '../hooks/useProductQuery';
import { useProductMutations } from '../hooks/useProductMutations';
import { ProductPlanPanel } from '../components/ProductPlanPanel';
import { ProductAddOnPanel } from '../components/ProductAddOnPanel';
import { ProductAddOnGroupPanel } from '../components/ProductAddOnGroupPanel';
import { ProductConfigSettingPanel } from '../components/ProductConfigSettingPanel';
import { ProductRulesPanel } from '../components/ProductRulesPanel';
import { ProductSecurityGroupPanel } from '../components/ProductSecurityGroupPanel';
import { ProductFeePanel } from '../components/ProductFeePanel';
import { SmriqCredentialsPanel } from '../components/SmriqCredentialsPanel';
import { SmriqPlanDefaultsPanel } from '../components/SmriqPlanDefaultsPanel';
import { SmriqVehicleEligibilityPanel } from '../components/SmriqVehicleEligibilityPanel';
import { ALL_PRODUCT_DETAIL_TAB_IDS, getProductDetailTabs, resolveProductDetailTab, type ProductDetailTab } from '../utils/productTabs';

function statusVariant(status?: string): 'success' | 'error' | 'info' {
  if (status === 'active') return 'success';
  if (status === 'inactive' || status === 'retired') return 'error';
  return 'info';
}

export function ProductDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as ProductDetailTab | null) ?? 'details';
  const [activeTab, setActiveTab] = useState<ProductDetailTab>(
    ALL_PRODUCT_DETAIL_TAB_IDS.includes(initialTab) ? initialTab : 'details',
  );
  const { hasPermission } = usePermissions();
  const query = useProductQuery(id);
  const { retireMutation, restoreMutation } = useProductMutations();
  const parentDealerQuery = useDealerQuery(query.data?.parent_dealer_id ?? '');

  if (!id) return <p className="text-mp-error">Invalid product ID.</p>;
  if (query.isLoading) return <p>Loading product...</p>;
  if (query.isError || !query.data) {
    return <Alert variant="error"><span>Product not found or failed to load.</span></Alert>;
  }

  const product = query.data;
  const isRetired = Boolean(product.deleted_at);
  const tabs = getProductDetailTabs(product.product_type?.key, {
    includeDocuments: hasPermission({ resource: 'document_assignment', action: 'browse' }),
  });
  const activeTabSafe = resolveProductDetailTab(activeTab, tabs);

  const handleRetire = () => {
    retireMutation.mutate(id, {
      onSuccess: () => navigate('/smr-admin/products'),
    });
  };

  const handleRestore = () => {
    restoreMutation.mutate(id);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <TextLink to="/smr-admin/products" color="secondary" className="text-sm">&larr; Back to Products</TextLink>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{product.name}</h1>
          {product.status_key && (
            <Badge size="xs" variant={statusVariant(product.status_key)}>{product.status_key}</Badge>
          )}
          {isRetired && (
            <Badge size="xs" variant="error">Retired</Badge>
          )}
        </div>
        {!isRetired && (
          <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
            <ButtonLink to={`/smr-admin/products/${id}/edit`} variant="outline" size="xs">
              Edit Product
            </ButtonLink>
          </PermissionGuard>
        )}
      </div>

      <Tabs
        tabs={tabs}
        activeTab={activeTabSafe}
        onTabChange={setActiveTab}
        idPrefix="product-detail"
        ariaLabel="Product sections"
      >
        {activeTabSafe === 'details' && (
          <div className="space-y-6 max-w-2xl">
            <h2 className="text-base font-medium text-mp-foreground">
              Key details for this Product
            </h2>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-mp-border">
                  <th className="px-3 py-2 text-left w-40">ID</th>
                  <td className="px-3 py-2 text-left font-mono text-xs">{product.id}</td>
                </tr>
                <tr className="border-b border-mp-border">
                  <th className="px-3 py-2 text-left">Name</th>
                  <td className="px-3 py-2 text-left font-semibold">{product.name}</td>
                </tr>
                <tr className="border-b border-mp-border">
                  <th className="px-3 py-2 text-left">Description</th>
                  <td className="px-3 py-2 text-left">{product.description || <span className="text-mp-muted">—</span>}</td>
                </tr>
                <tr className="border-b border-mp-border">
                  <th className="px-3 py-2 text-left">Status</th>
                  <td className="px-3 py-2 text-left">
                    <Badge size="xs" variant={statusVariant(product.status_key)}>
                      {product.status_key}
                    </Badge>
                  </td>
                </tr>
                {product.effective_from && (
                  <tr className="border-b border-mp-border">
                    <th className="px-3 py-2 text-left">Effective From</th>
                    <td className="px-3 py-2 text-left">{new Date(product.effective_from).toLocaleDateString()}</td>
                  </tr>
                )}
                {product.parent_dealer_id && (
                  <tr className="border-b border-mp-border">
                    <th className="px-3 py-2 text-left">Parent Dealer</th>
                    <td className="px-3 py-2 text-left">
                      {parentDealerQuery.isLoading && <span className="text-mp-muted text-sm">Loading...</span>}
                      {parentDealerQuery.data
                        ? <TextLink to={`/admin/dealers/${parentDealerQuery.data.id}`} color="secondary" className="text-sm">{parentDealerQuery.data.name}</TextLink>
                        : !parentDealerQuery.isLoading && <span className="text-mp-muted text-sm">—</span>}
                    </td>
                  </tr>
                )}
                <tr className="border-b border-mp-border">
                  <th className="px-3 py-2 text-left">Created</th>
                  <td className="px-3 py-2 text-left">{new Date(product.created_at).toLocaleString()}</td>
                </tr>
                <tr className="border-b border-mp-border">
                  <th className="px-3 py-2 text-left">Updated</th>
                  <td className="px-3 py-2 text-left">{new Date(product.updated_at).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>

            <PermissionGuard permission={{ resource: 'smr', action: 'product.delete' }}>
              <div className="border-t border-mp-border pt-6">
                <h3 className="font-semibold mb-2">{isRetired ? 'Restore Product' : 'Retire Product'}</h3>
                <div className="flex items-start gap-4 justify-between">
                  <p className="text-sm text-mp-muted">
                    {isRetired
                      ? 'This product is retired and hidden from active use. Restoring it makes it available again.'
                      : 'Retiring a product removes it from active use. It can be restored later.'}
                  </p>
                  {isRetired ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={handleRestore}
                      disabled={restoreMutation.isPending}
                      className="whitespace-nowrap"
                    >
                      {restoreMutation.isPending ? 'Restoring...' : 'Restore Product'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="warning"
                      type="button"
                      onClick={handleRetire}
                      disabled={retireMutation.isPending}
                      className="whitespace-nowrap"
                    >
                      {retireMutation.isPending ? 'Retiring...' : 'Retire Product'}
                    </Button>
                  )}
                </div>
              </div>
            </PermissionGuard>
          </div>
        )}
        {activeTabSafe === 'plans' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the Plans assigned to this Product
            </h2>
            <ProductPlanPanel productId={id} />
          </>
        )}
        {activeTabSafe === 'smriq-credentials' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              SMRiQ API credentials used to source this Product&apos;s services
            </h2>
            <SmriqCredentialsPanel productId={id} />
            <div className="mt-8 border-t border-mp-border pt-6">
              <SmriqPlanDefaultsPanel productId={id} settings={product.config_settings ?? []} />
            </div>
            <div className="mt-8 border-t border-mp-border pt-6">
              <h2 className="mb-4 text-base font-medium text-mp-foreground">
                Which vehicles this Product applies to
              </h2>
              <SmriqVehicleEligibilityPanel systemPlanId={product.system_plan_id} />
            </div>
          </>
        )}
        {activeTabSafe === 'add-ons' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the Optional Extras available on this Product
            </h2>
            <ProductAddOnPanel productId={id} configSettings={product.config_settings} />
          </>
        )}
        {activeTabSafe === 'add-on-groups' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the Optional Extra Groups available on this Product
            </h2>
            <ProductAddOnGroupPanel productId={id} />
          </>
        )}
        {activeTabSafe === 'config-options' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the Configuration Settings for this Product
            </h2>
            <ProductConfigSettingPanel productId={id} product={product} />
          </>
        )}
        {activeTabSafe === 'rules' && <ProductRulesPanel productId={id} product={product} />}
        {activeTabSafe === 'fees' && (
          <PermissionGuard permission={{ resource: 'smr', action: 'product_fee.browse' }}>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the Fees applied to this Product
            </h2>
            <ProductFeePanel productId={id} />
          </PermissionGuard>
        )}
        {activeTabSafe === 'security-groups' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the Security Groups this Product is assigned to
            </h2>
            <ProductSecurityGroupPanel productId={id} productName={product.name} parentDealerId={product.parent_dealer_id ?? null} />
          </>
        )}
        {activeTabSafe === 'documents' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              These are the document templates sent for this Product
            </h2>
            <ProductDocumentsPanel productId={id} />
          </>
        )}
      </Tabs>
    </section>
  );
}
